// 产品辅助函数
import { buildProductCategoryPath } from '@/components/layout/utils/navMenu'
import { isPathUnderMenu, normalizeContentPath, normalizeMenuPath } from '@/components/portal/common/utils/menuPath'

const CATEGORY_ICONS = {
  'lightning-rod': 'M12 2L4 14h6l-1 8 10-14h-6l-1-6z',
  grounding: 'M12 3v18M5 12h14M8 8l8 8M16 8l-8 8',
  'power-spd': 'M13 2L3 14h8l-1 10 10-14h-8l1-8z',
  ssd: 'M4 7h16v10H4z M8 11h8',
  'signal-spd': 'M2 12h4l3-9 4 18 3-9h6',
  'antenna-spd': 'M12 2v4M8 6l2 2M16 6l-2 2M5 12a7 7 0 0014 0',
  'combined-spd': 'M4 8h16v8H4z M8 12h8',
  'smart-spd': 'M9 3h6v4H9z M5 7h14v12H5z M9 13h2M13 13h2',
  'fault-location': 'M12 2a10 10 0 100 20 10 10 0 000-20z M12 6v6l4 2',
  'lightning-monitor': 'M12 3a9 9 0 100 18 9 9 0 000-18z M12 7v5l3 2',
  'strike-counter': 'M7 20h10M12 4v12M9 8l3-3 3 3',
  'lightning-warning': 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  pdu: 'M4 6h16v12H4z M8 10h8M8 14h4',
  'power-filter': 'M8 8h8v8H8z M12 8v8',
  magnetic: 'M6 6h12v12H6z M9 9h6v6H9z',
  circulator: 'M12 2a10 10 0 100 20 10 10 0 000-20z M8 12h8M12 8v8',
  all: 'M4 6h16v12H4z M8 10h8M8 14h4'
}

/** 根据分类 slug 返回 SVG path 图标数据 */
export function getCategoryIconPath(slug) {
  return CATEGORY_ICONS[slug] || 'M12 2a10 10 0 100 20 10 10 0 000-20z'
}

/** 构建产品分类页路由路径 */
export function buildCategoryPath(slug, productsRootPath = '') {
  const base = normalizeMenuPath(productsRootPath)
  if (!base) return slug ? `/products/category/${slug}` : '/products'
  return slug ? `${base}/category/${slug}` : base
}

const PRODUCT_CATEGORY_SUFFIX = /\/category\/[^/]+$/

/** 去掉路径末尾的产品分类段，得到产品列表根路径 */
export function stripProductCategorySegment(path) {
  return normalizeContentPath(path).replace(PRODUCT_CATEGORY_SUFFIX, '')
}

/** 同页切换产品分类（仅 category 段变化）时不应滚回顶部 */
export function isProductCategoryInPageSwitch(toPath, fromPath) {
  const to = normalizeContentPath(toPath)
  const from = normalizeContentPath(fromPath)
  if (!to || !from || to === from) return false

  const toBase = stripProductCategorySegment(to)
  const fromBase = stripProductCategorySegment(from)
  if (toBase !== fromBase) return false

  return PRODUCT_CATEGORY_SUFFIX.test(to)
    || PRODUCT_CATEGORY_SUFFIX.test(from)
    || (to === toBase && from !== toBase)
    || (from === fromBase && to !== fromBase)
}

/** 判断路径是否为产品相关页（列表或分类） */
export function isProductsPath(path, productsRootPath = '') {
  const current = normalizeContentPath(path)
  const base = normalizeMenuPath(productsRootPath)
  if (!base) {
    return current === '/products' || /^\/products\/category\/[^/]+$/.test(current)
  }
  if (current === base) return true
  return current.startsWith(`${base}/category/`)
}

/** 判断路径是否为旧版产品目录滚动页（备份） */
export function isProductCatalogPath(path) {
  return /^\/products\/catalog(?:\/[^/]+)?$/.test(normalizeContentPath(path))
}

/** 判断路径是否为单产品详情页 */
export function isProductDetailPath(path) {
  return /^\/products\/detail\/[^/]+$/.test(normalizeContentPath(path))
}

/** 从路由路径解析当前激活的产品分类 slug */
export function resolveProductCategoryFromPath(path, productsRootPath = '') {
  const current = normalizeContentPath(path)
  const base = normalizeMenuPath(productsRootPath)
  if (!base) {
    if (current === '/products') return ''
    const match = current.match(/^\/products\/category\/([^/]+)$/)
    return match ? match[1] : ''
  }
  if (current === base) return ''
  const prefix = `${base}/category/`
  if (!current.startsWith(prefix)) return ''
  return current.slice(prefix.length).split('/')[0] || ''
}

/** 判断路径是否属于产品栏目 */
export function isPathUnderProductsRoot(path, productsRootPath = '') {
  const base = normalizeMenuPath(productsRootPath)
  if (!base) return isProductsPath(path)
  return isPathUnderMenu(path, base) || isProductDetailPath(path)
}

/** 生成分类数据的 Vuex loading key */
export function getProductCategoryLoadingKey(categoryId = '') {
  return categoryId ? String(categoryId) : 'all'
}

function isBlankParentId(parentId) {
  return parentId == null || parentId === ''
}

function sortCategoryNodes(items = []) {
  items.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
  for (const item of items) {
    if (item.children?.length) sortCategoryNodes(item.children)
  }
  return items
}

/** 展平分类树或扁平列表，保留 parentId，去掉嵌套 children */
export function flattenPortalCategories(categories = [], inheritedParentId = null, acc = []) {
  for (const item of categories || []) {
    if (!item) continue
    const parentId = item.parentId ?? inheritedParentId
    acc.push({ ...item, parentId, children: [] })
    flattenPortalCategories(item.children || [], item.id, acc)
  }
  return acc
}

/**
 * 按 parentId 重建完整分类树。
 * 兼容接口只嵌套到二级、三级以 parentId 挂在列表里的情况。
 */
export function rebuildPortalCategoryTree(list = []) {
  if (!Array.isArray(list) || !list.length) return []
  const byId = new Map()
  for (const node of flattenPortalCategories(list)) {
    if (node.id == null || node.id === '') continue
    byId.set(String(node.id), { ...node, children: [] })
  }
  const roots = []
  for (const node of byId.values()) {
    const parentId = node.parentId
    if (isBlankParentId(parentId) || !byId.has(String(parentId))) {
      roots.push(node)
      continue
    }
    byId.get(String(parentId)).children.push(node)
  }
  return sortCategoryNodes(roots)
}

function filterEnabledCategoryTree(nodes = []) {
  return (nodes || [])
    .filter((item) => item && item.enabled !== false)
    .map((item) => ({
      ...item,
      children: filterEnabledCategoryTree(item.children || []),
    }))
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
}

/** 门户启用中的完整分类树（含三级及更深层级） */
export function getEnabledPortalCategoryTree(list = []) {
  return filterEnabledCategoryTree(rebuildPortalCategoryTree(list))
}

/** 在门户分类树中按 id 递归查找分类 */
export function findPortalCategory(categories, activeKey) {
  if (!activeKey || !categories?.length) return null
  const key = String(activeKey)
  for (const item of categories) {
    if (String(item.id) === key) return item
    const child = findPortalCategory(item.children || [], key)
    if (child) return child
  }
  return null
}

/** 查找分类所属的一级父分类；本身为一级则返回自身 */
export function findPortalCategoryRoot(categories, activeKey) {
  let current = findPortalCategory(categories, activeKey)
  if (!current) return null
  const seen = new Set()
  while (current.parentId != null && current.parentId !== '') {
    const key = String(current.id)
    if (seen.has(key)) break
    seen.add(key)
    const parent = findPortalCategory(categories, current.parentId)
    if (!parent) break
    current = parent
  }
  return current
}

/** 收集分类自身及所有子孙 id */
export function collectCategoryIds(category) {
  if (!category) return []
  const ids = [String(category.id)]
  for (const child of category.children || []) {
    ids.push(...collectCategoryIds(child))
  }
  return ids
}

/** 校验并返回有效的门户分类 key */
export function resolvePortalCategoryKey(key, portalCategories) {
  if (!key) return ''
  if (findPortalCategory(portalCategories, key)) return String(key)
  return String(key)
}

/** 获取默认选中的分类 key（列表首项） */
export function getDefaultCategoryKey(portalCategories) {
  if (portalCategories?.length) return String(portalCategories[0].id)
  return ''
}

/** 判断顶部分类条中的一级分类是否为当前激活项 */
export function isPortalCategoryActive(cat, activeKey, categories = []) {
  if (!activeKey || !cat) return false
  if (String(cat.id) === String(activeKey)) return true
  const root = findPortalCategoryRoot(categories, activeKey)
  return !!(root && String(root.id) === String(cat.id))
}

/** 获取分类条目的唯一 key */
export function getCategoryItemKey(cat) {
  return String(cat.id)
}

/** 从当前分类向上收集祖先链（一级在前） */
export function getCategoryAncestorChain(categories, activeKey) {
  const current = findPortalCategory(categories, activeKey)
  if (!current) return []
  const chain = []
  let node = current
  const seen = new Set()
  while (node) {
    chain.unshift(node)
    if (node.parentId == null || node.parentId === '') break
    const key = String(node.id)
    if (seen.has(key)) break
    seen.add(key)
    node = findPortalCategory(categories, node.parentId)
  }
  return chain
}

function sortEnabledCategoryChildren(node) {
  return (node?.children || [])
    .filter((item) => item.enabled !== false)
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
}

function toCategoryPills(parent, children, allLabel = '全部') {
  return [
    { slug: String(parent.id), name: allLabel },
    ...children.map((child) => ({ slug: String(child.id), name: child.name }))
  ]
}

/** 当前一级下的二级标签列表（含「全部」指向一级本身） */
export function getSubCategoryPills(categories, activeKey, options = {}) {
  const rows = getCategoryPillRows(categories, activeKey, options)
  return rows[0]?.pills || []
}

/** 当前二级 pill 高亮 key：选中更深层级时回退到该一级下的直接子分类 */
export function getSecondaryCategoryKey(categories, activeKey) {
  const rows = getCategoryPillRows(categories, activeKey)
  return rows[0]?.activeKey || String(activeKey || '')
}

/**
 * 产品指南页：一级顶栏之下，沿祖先链逐级展示子分类 pill。
 * 选中到第 N 级且该级仍有子分类时，会再出第 N+1 级（「全部」高亮当前级）。
 */
export function getCategoryPillRows(categories, activeKey, options = {}) {
  const allLabel = options.allLabel || '全部'
  const levelLabelFn = options.levelLabelFn || categoryLevelLabel
  const levelAriaFn = options.levelAriaFn || ((label) => `${label}分类`)
  const chain = getCategoryAncestorChain(categories, activeKey)
  if (!chain.length) return []

  const rows = []
  for (let i = 0; i < chain.length; i += 1) {
    const parent = chain[i]
    const children = sortEnabledCategoryChildren(parent)
    if (!children.length) continue
    const next = chain[i + 1]
    const level = i + 2
    const levelLabel = levelLabelFn(level)
    rows.push({
      parentId: parent.id,
      activeKey: next ? String(next.id) : String(parent.id),
      ariaLabel: levelAriaFn(levelLabel, level),
      pills: toCategoryPills(parent, children, allLabel)
    })
  }
  return rows
}

/** 当前二级下的三级标签（含「全部」指向该二级）；无三级时为空 */
export function getThirdCategoryPills(categories, activeKey) {
  const rows = getCategoryPillRows(categories, activeKey)
  return rows[1]?.pills || []
}

const EMPTY_DRAWER_STATE = {
  visible: false,
  breadcrumbs: [],
  current: null
}

const CATEGORY_LEVEL_LABELS = ['一级', '二级', '三级', '四级', '五级', '六级', '七级', '八级', '九级', '十级']

/** 分类层级展示名（level 从 1 起，1=一级） */
export function categoryLevelLabel(level) {
  if (level == null || level < 1) return ''
  return CATEGORY_LEVEL_LABELS[level - 1] || `${level}级`
}

function buildDrawerBreadcrumb(node, level, levelLabelFn) {
  const resolveLevel = levelLabelFn || categoryLevelLabel
  return {
    id: String(node.id),
    name: node.name,
    level,
    levelLabel: resolveLevel(level)
  }
}

function buildDrawerCurrent(parent, level, activeKey, children, options = {}) {
  if (!children.length) return null
  const allLabel = options.allLabel || '全部'
  const levelLabelFn = options.levelLabelFn || categoryLevelLabel
  const levelAriaFn = options.levelAriaFn || ((label) => `${label}分类`)
  const parentId = String(parent.id)
  const key = String(activeKey)
  const levelLabel = levelLabelFn(level)
  return {
    parentId: parent.id,
    activeKey: key,
    pills: toCategoryPills(parent, children, allLabel),
    level,
    levelLabel,
    isParentSelected: key === parentId,
    ariaLabel: levelAriaFn(levelLabel, level)
  }
}

/**
 * 产品指南页：抽屉式级联分类导航状态。
 * expandAtLevel：强制展开某一级完整列表（breadcrumb 返回时使用）。
 * allLabel / levelLabelFn / levelAriaFn：多语言文案。
 */
export function getCategoryDrawerState(categories, activeKey, options = {}) {
  const { expandAtLevel = null, allLabel = '全部', levelLabelFn, levelAriaFn } = options
  const labelOptions = { allLabel, levelLabelFn, levelAriaFn }
  const resolveLevel = levelLabelFn || categoryLevelLabel
  if (!categories?.length) return EMPTY_DRAWER_STATE

  const chain = getCategoryAncestorChain(categories, activeKey)
  if (!chain.length) return EMPTY_DRAWER_STATE

  const root = chain[0]
  const l2Children = sortEnabledCategoryChildren(root)
  if (!l2Children.length) return EMPTY_DRAWER_STATE

  const activeNode = chain[chain.length - 1]
  const chainDepth = chain.length

  if (expandAtLevel != null && expandAtLevel >= 2) {
    const parentIndex = expandAtLevel - 2
    if (parentIndex >= 0 && parentIndex < chain.length) {
      const parent = chain[parentIndex]
      const children = sortEnabledCategoryChildren(parent)
      const current = buildDrawerCurrent(
        parent,
        expandAtLevel,
        chain.length >= expandAtLevel ? chain[expandAtLevel - 1].id : parent.id,
        children,
        labelOptions
      )
      if (!current) return EMPTY_DRAWER_STATE

      const breadcrumbs = chain
        .slice(1, expandAtLevel - 1)
        .map((node, idx) => buildDrawerBreadcrumb(node, idx + 2, resolveLevel))

      return {
        visible: true,
        breadcrumbs,
        current
      }
    }
  }

  const activeChildren = sortEnabledCategoryChildren(activeNode)
  if (activeChildren.length > 0) {
    const breadcrumbs = chain
      .slice(1)
      .map((node, idx) => buildDrawerBreadcrumb(node, idx + 2, resolveLevel))

    const current = buildDrawerCurrent(
      activeNode,
      chainDepth + 1,
      activeNode.id,
      activeChildren,
      labelOptions
    )
    if (!current) return EMPTY_DRAWER_STATE

    return {
      visible: true,
      breadcrumbs,
      current
    }
  }

  if (chainDepth === 1) {
    const current = buildDrawerCurrent(root, 2, root.id, l2Children, labelOptions)
    return {
      visible: true,
      breadcrumbs: [],
      current
    }
  }

  const parent = chain[chainDepth - 2]
  const siblings = sortEnabledCategoryChildren(parent)
  const breadcrumbs = chain
    .slice(1, -1)
    .map((node, idx) => buildDrawerBreadcrumb(node, idx + 2, resolveLevel))

  const current = buildDrawerCurrent(parent, chainDepth, activeNode.id, siblings, labelOptions)
  if (!current) return EMPTY_DRAWER_STATE

  return {
    visible: true,
    breadcrumbs,
    current
  }
}

/** 门户分类展示名：按祖先链拼接 */
export function formatPortalCategoryLabel(category, categories = []) {
  if (!category) return ''
  const path = category.ancestorPath
  if (Array.isArray(path) && path.length > 1) {
    return path.join('-')
  }
  const names = []
  let current = category
  const seen = new Set()
  while (current) {
    if (current.name) names.unshift(current.name)
    if (current.parentId == null || current.parentId === '') break
    const key = String(current.id ?? '')
    if (key && seen.has(key)) break
    if (key) seen.add(key)
    current = findPortalCategory(categories, current.parentId)
  }
  return names.join('-') || category.name || path?.[0] || ''
}

/** 产品目录侧栏/主区分组：按分类树递归挂子组 */
function buildCatalogSubgroups(nodes = [], products = []) {
  return (nodes || [])
    .filter((child) => child.enabled !== false)
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((child) => {
      const nested = (child.children || []).filter((item) => item.enabled !== false)
      const nestedGroups = buildCatalogSubgroups(nested, products)
      const nestedIds = new Set(nested.flatMap((item) => collectCategoryIds(item)))
      const ownProducts = filterProductsByCategoryTree(products, child)
        .filter((product) => !nestedIds.has(String(product.categoryId)))

      return {
        id: child.id,
        name: child.name,
        products: ownProducts,
        subgroups: nestedGroups
      }
    })
    .filter((sub) => sub.products.length > 0 || sub.subgroups.length > 0)
}

export function buildCatalogCategoryGroups(categories = [], products = []) {
  return (categories || [])
    .map((category) => {
      const children = (category.children || []).filter((child) => child.enabled !== false)
      const childIds = new Set(children.flatMap((child) => collectCategoryIds(child)))
      const directProducts = filterProductsByCategoryId(products, category.id)
        .filter((item) => !childIds.has(String(item.categoryId)))
      const subgroups = buildCatalogSubgroups(children, products)

      return {
        id: category.id,
        name: category.name,
        products: directProducts,
        subgroups
      }
    })
    .filter((group) => group.products.length > 0 || group.subgroups.length > 0)
}

export function filterCatalogGroupsByKeyword(groups = [], matchProduct) {
  const walk = (group) => {
    const products = (group.products || []).filter((item) => matchProduct(item))
    const subgroups = (group.subgroups || [])
      .map((sub) => walk(sub))
      .filter((sub) => sub.products.length > 0 || sub.subgroups.length > 0)
    return { ...group, products, subgroups }
  }
  return (groups || [])
    .map((group) => walk(group))
    .filter((group) => group.products.length > 0 || group.subgroups.length > 0)
}

export function flattenCatalogGroupProducts(groups = [], resolveCategoryName) {
  const result = []
  const walk = (group, prefix) => {
    const label = prefix ? `${prefix}-${group.name}` : group.name
    for (const product of group.products || []) {
      result.push({
        product,
        categoryName: resolveCategoryName ? resolveCategoryName(product, label) : label
      })
    }
    for (const sub of group.subgroups || []) walk(sub, label)
  }
  for (const group of groups || []) walk(group, '')
  return result
}

export function flattenCatalogSubgroups(subgroups = [], depth = 1, ancestorIds = []) {
  const rows = []
  for (const sub of subgroups || []) {
    rows.push({
      id: sub.id,
      name: sub.name,
      products: sub.products || [],
      depth,
      ancestorIds
    })
    rows.push(...flattenCatalogSubgroups(sub.subgroups, depth + 1, [...ancestorIds, String(sub.id)]))
  }
  return rows
}

/** 构建产品详情页路由路径 */
export function buildDetailPath(id) {
  return `/products/detail/${id}`
}

/** 构建旧版产品目录备份页路径 */
export function buildCatalogPath(id) {
  return id ? `/products/catalog/${id}` : '/products/catalog'
}

/** 返回产品展示名称 */
export function displayProductName(item) {
  return item.title || ''
}

/** 按分类 ID 过滤产品列表 */
export function filterProductsByCategoryId(products, categoryId) {
  if (!categoryId) return products || []
  return (products || []).filter((item) => String(item.categoryId) === String(categoryId))
}

/** 按分类及其子孙过滤产品列表 */
export function filterProductsByCategoryTree(products, category) {
  if (!category) return products || []
  const idSet = new Set(collectCategoryIds(category))
  return (products || []).filter((item) => idSet.has(String(item.categoryId)))
}

function sortProductsByTitle(items = []) {
  return [...items].sort((a, b) =>
    String(a.title || '').localeCompare(String(b.title || ''), 'zh-CN')
  )
}

function toMegaProductLink(product) {
  return {
    name: displayProductName(product),
    path: buildDetailPath(product.id)
  }
}

function navChildToCategoryTreeNode(child) {
  return {
    id: child.id,
    children: (child.children || []).map(navChildToCategoryTreeNode)
  }
}

/**
 * 产品 mega 菜单右侧栏：只展示分类
 * - 二级名加粗作为列标题
 * - 三级作为标题下链接
 * - 四级及更深层级不展示
 * - 无子分类时仅显示二级名称
 */
export function buildProductMegaCategoryColumns(category, productsRootPath = '') {
  if (!category?.id) return []

  const toItems = (nodes = []) => (nodes || [])
    .filter((item) => item && item.enabled !== false)
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((item) => ({
      name: item.name,
      path: item.path || buildProductCategoryPath(item.id, productsRootPath),
      children: []
    }))

  return (category.children || [])
    .filter((child) => child && child.enabled !== false)
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((secondary) => ({
      heading: {
        kind: 'category',
        name: secondary.name,
        path: secondary.path || buildProductCategoryPath(secondary.id, productsRootPath)
      },
      items: toItems(secondary.children || [])
    }))
}

/**
 * 产品指南 mega menu 右侧栏：
 * - 有二级分类：二级名加粗 + 下列常规字重产品名
 * - 无二级：直接展示常规字重产品名
 * - 一级下同时存在二级产品与直挂产品：分列展示
 */
export function buildProductMegaColumns(category, products = [], productsRootPath = '') {
  if (!category?.id) return []

  const secondaryChildren = (category.children || []).filter(Boolean)
  const columns = []

  const pushCategoryColumn = (secondary, secondaryProducts) => {
    if (!secondaryProducts.length) return
    columns.push({
      heading: {
        kind: 'category',
        name: secondary.name,
        path: secondary.path || buildProductCategoryPath(secondary.id, productsRootPath)
      },
      items: sortProductsByTitle(secondaryProducts).map(toMegaProductLink)
    })
  }

  const pushDirectProductColumn = (product) => {
    columns.push({
      heading: {
        kind: 'product',
        name: displayProductName(product),
        path: buildDetailPath(product.id)
      },
      items: []
    })
  }

  if (!secondaryChildren.length) {
    sortProductsByTitle(filterProductsByCategoryId(products, category.id))
      .forEach(pushDirectProductColumn)
    return columns
  }

  const childCategoryIds = new Set(
    secondaryChildren.flatMap((child) => collectCategoryIds(navChildToCategoryTreeNode(child)))
  )

  secondaryChildren.forEach((secondary) => {
    const secondaryProducts = filterProductsByCategoryTree(
      products,
      navChildToCategoryTreeNode(secondary)
    )
    pushCategoryColumn(secondary, secondaryProducts)
  })

  sortProductsByTitle(
    filterProductsByCategoryId(products, category.id).filter(
      (product) => !childCategoryIds.has(String(product.categoryId))
    )
  ).forEach(pushDirectProductColumn)

  return columns
}

/** 从扁平产品池取某一级分类（含子孙）下的产品，去重后可选截断 */
export function collectProductsForPrimaryCategory(productPool, categoryId, categoryTree = [], limit = 0) {
  if (!categoryId || !productPool?.length) return []

  const primary = findPortalCategory(categoryTree, categoryId)
  const allowedIds = primary
    ? new Set(collectCategoryIds(primary))
    : new Set([String(categoryId)])

  const merged = []
  const seen = new Set()
  for (const product of productPool) {
    if (!allowedIds.has(String(product.categoryId))) continue
    const key = String(product.id ?? product.slug ?? '')
    if (!key || seen.has(key)) continue
    seen.add(key)
    merged.push(product)
  }

  if (limit > 0) return merged.slice(0, limit)
  return merged
}

/** 合并 productGroups 中属于某一级分类（含子孙）的产品 */
export function mergeModuleProductsForPrimary(productGroups, categoryId, categoryTree = [], limit = 0) {
  if (!categoryId || !productGroups?.length) return []

  const primary = findPortalCategory(categoryTree, categoryId)
  const allowedIds = primary
    ? new Set(collectCategoryIds(primary))
    : new Set([String(categoryId)])

  const merged = []
  const seen = new Set()
  for (const group of productGroups) {
    if (!allowedIds.has(String(group.categoryId))) continue
    for (const product of group.products || []) {
      const key = String(product.id ?? product.slug ?? '')
      if (!key || seen.has(key)) continue
      seen.add(key)
      merged.push(product)
    }
  }

  if (limit > 0) return merged.slice(0, limit)
  return merged
}

/** 根据激活 slug 和门户分类解析产品列表 */
export function getProductsByActiveSlug(activeSlug, products = [], portalCategories = []) {
  if (!activeSlug) return products || []

  const category = findPortalCategory(portalCategories, activeSlug)
  if (!category) {
    return filterProductsByCategoryId(products, activeSlug)
  }

  if (category.children?.length) {
    return filterProductsByCategoryTree(products, category)
  }
  return filterProductsByCategoryId(products, category.id)
}

/**
 * 选中「全部产品」时按一级分类分组；
 * 选中有子分类时按直接子分类分组；
 * 其他情况返回 null，由调用方使用扁平列表。
 */
export function getProductGroupsByActiveSlug(activeSlug, products = [], portalCategories = []) {
  if (!activeSlug) {
    const primaries = (portalCategories || []).filter(
      (item) => item.enabled !== false && (item.parentId == null || item.parentId === '')
    )
    const groupedIds = new Set()
    const groups = primaries
      .slice()
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((primary) => {
        const list = filterProductsByCategoryTree(products, primary)
        list.forEach((item) => groupedIds.add(String(item.id)))
        return {
          id: primary.id,
          name: primary.name,
          products: list
        }
      })
      .filter((group) => group.products.length > 0)

    const uncategorized = (products || []).filter((item) => !groupedIds.has(String(item.id)))
    if (!groups.length && !uncategorized.length) return null
    return { groups, directProducts: uncategorized }
  }

  const category = findPortalCategory(portalCategories, activeSlug)
  if (!category?.children?.length) return null

  const groups = (category.children || [])
    .filter((child) => child.enabled !== false)
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((child) => ({
      id: child.id,
      name: child.name,
      products: filterProductsByCategoryTree(products, child)
    }))
    .filter((group) => group.products.length > 0)

  const childIds = new Set(
    (category.children || []).flatMap((child) => collectCategoryIds(child))
  )
  const directProducts = filterProductsByCategoryId(products, category.id)
    .filter((item) => !childIds.has(String(item.categoryId)))
  if (!groups.length && !directProducts.length) return null

  return { groups, directProducts }
}

export const PRODUCT_LINK_PREFIX = '跳转至产品：'
const DEVICE_JUMP_PREFIX_RE = /^跳转至\s*(?:产品|分类)\s*[：:]\s*/

/** 去掉设备文字链上的「跳转至产品/分类：」 */
export function stripProductLinkPrefix(text) {
  return String(text || '').replace(DEVICE_JUMP_PREFIX_RE, '')
}

export function isProductDeviceLink(attrs = {}) {
  return attrs['data-link-type'] === 'product' || String(attrs.href || '').startsWith('/products/detail/')
}

export function isProductCategoryLink(attrs = {}) {
  return attrs['data-link-type'] === 'product-category' || String(attrs.href || '').startsWith('/products/category/')
}

/** 正文 HTML：设备文字链锚文本去掉前缀 */
export function stripProductLinkPrefixInHtml(html) {
  const source = String(html || '')
  if (!source) return source
  return source.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (full, attrs, inner) => {
    const attrText = String(attrs || '')
    const isDevice = /data-link-type=["']product(?:-category)?["']/i.test(attrText)
      || /\/products\/(?:detail|category)\//i.test(attrText)
    if (!isDevice) return full
    const next = String(inner || '').replace(/(^|>)(\s*)跳转至\s*(?:产品|分类)\s*[：:]\s*/g, '$1$2')
    return next === inner ? full : `<a${attrs}>${next}</a>`
  })
}

/** 从富文本链接标签中解析产品名称 */
export function parseProductLinkLabel(label) {
  const text = String(label || '').trim()
  const stripped = stripProductLinkPrefix(text)
  if (stripped !== text) return stripped
  if (text.startsWith(PRODUCT_LINK_PREFIX)) return text.slice(PRODUCT_LINK_PREFIX.length).trim()
  return ''
}

/** 从产品详情 href 中提取产品 ID */
export function extractProductIdFromHref(href) {
  const match = String(href || '').match(/\/products\/detail\/(\d+)/)
  return match ? match[1] : ''
}

/** 在产品索引中按内容 ID 查找产品 */
export function findProductInIndex(products, id) {
  if (!id) return null
  return (products || []).find((item) => String(item.id) === String(id)) || null
}

/** 在产品索引中按 groupId 查找当前语言产品 */
export function findProductByGroupId(products, groupId) {
  if (groupId == null || groupId === '') return null
  return (products || []).find((item) => String(item.groupId ?? '') === String(groupId)) || null
}

/** 在产品索引中按标题模糊匹配查找产品 */
export function findProductByTitle(products, title) {
  const name = String(title || '').trim()
  if (!name || !products?.length) return null
  const exact = products.find((item) => item.title === name)
  if (exact) return exact
  return products.find((item) => {
    const itemTitle = String(item.title || '')
    return itemTitle.includes(name) || name.includes(itemTitle)
  }) || null
}

/** 根据 ID（含其他语种 content id / groupId）或链接标签解析当前语言产品 */
export function resolveProductLinkTarget(products, {
  id,
  label,
  title,
  coverUrl,
  categoryId,
} = {}) {
  const byId = findProductInIndex(products, id)
  if (byId) return byId

  const byGroup = findProductByGroupId(products, id)
  if (byGroup) return byGroup

  const labelName = parseProductLinkLabel(label)
  const siblingId = resolveLocaleProductSibling(products, {
    currentId: id,
    groupId: id,
    title: String(title || '').trim() || labelName,
    coverUrl,
    categoryId,
  })
  if (siblingId) {
    const sibling = findProductInIndex(products, siblingId)
    if (sibling) return sibling
  }

  if (labelName) {
    return findProductByTitle(products, labelName)
  }
  const fallbackTitle = String(title || '').trim()
  if (fallbackTitle) {
    return findProductByTitle(products, fallbackTitle)
  }
  return null
}

function normalizeCoverKey(url) {
  const value = String(url || '').trim()
  if (!value) return ''
  try {
    const path = value.startsWith('http') ? new URL(value).pathname : value
    return path.replace(/\/+$/, '') || value
  } catch (_) {
    return value
  }
}

/**
 * 切换站点语言后，在新语言产品索引中解析同组产品 ID。
 * 优先 groupId，其次同分类下标题/封面匹配（索引暂无 groupId 时的兜底）。
 */
export function resolveLocaleProductSibling(products, {
  currentId,
  groupId,
  title,
  coverUrl,
  categoryId
} = {}) {
  const items = products || []
  if (!items.length) return ''

  if (currentId && findProductInIndex(items, currentId)) {
    return String(currentId)
  }

  if (groupId != null && groupId !== '') {
    const byGroup = items.find((item) => String(item.groupId ?? '') === String(groupId))
    if (byGroup?.id != null) return String(byGroup.id)
  }

  const cat = categoryId != null && categoryId !== '' ? String(categoryId) : ''
  const pool = cat
    ? items.filter((item) => String(item.categoryId) === cat)
    : items
  const searchPool = pool.length ? pool : items

  const normalizedTitle = String(title || '').trim()
  if (normalizedTitle) {
    const byTitle = searchPool.find((item) => String(item.title || '').trim() === normalizedTitle)
    if (byTitle?.id != null) return String(byTitle.id)
  }

  const coverKey = normalizeCoverKey(coverUrl)
  if (coverKey) {
    const byCover = searchPool.find((item) => {
      const itemCover = normalizeCoverKey(item.coverUrl || item.image)
      return itemCover && (itemCover === coverKey || itemCover.endsWith(coverKey) || coverKey.endsWith(itemCover))
    })
    if (byCover?.id != null) return String(byCover.id)
  }

  return ''
}

function sortEnabledCategories(list = []) {
  return (list || [])
    .filter((item) => item && item.enabled !== false)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
}

/** 取某父级下的直接子分类；parentId 为空时取一级分类 */
export function getEnabledCategoryChildren(categories = [], parentId = null) {
  if (isBlankParentId(parentId)) {
    return sortEnabledCategories(
      (categories || []).filter((item) => isBlankParentId(item.parentId))
    )
  }
  const parent = findPortalCategory(categories, parentId)
  const nested = sortEnabledCategories(parent?.children || [])
  if (nested.length) return nested
  return sortEnabledCategories(
    flattenPortalCategories(categories).filter((item) => String(item.parentId) === String(parentId))
  )
}

export function categoryHasEnabledChildren(category) {
  return sortEnabledCategories(category?.children || []).length > 0
}

function formatPortalCardIndex(index) {
  return String(index + 1).padStart(2, '0')
}

/** 将分类转为入口卡片数据（标题=当前分类，链接=直接下级） */
export function buildCategoryPortalCards(categories = [], parentId = null, productsRootPath = '', childLinkLimit = 0) {
  const siblings = getEnabledCategoryChildren(categories, parentId)
  const limit = Number(childLinkLimit) || 0
  return siblings.map((category, index) => {
    const children = sortEnabledCategories(category.children || [])
    const visible = limit > 0 ? children.slice(0, limit) : children
    return {
      id: category.id,
      index: formatPortalCardIndex(index),
      name: category.name || '',
      backgroundUrl: category.backgroundUrl || '',
      learnMorePath: buildCategoryPath(category.id, productsRootPath),
      children: visible.map((child) => ({
        id: child.id,
        name: child.name || '',
        path: buildCategoryPath(child.id, productsRootPath)
      }))
    }
  })
}

